// THE pantry: what you have, by aisle; ingredient expiry estimates; utensils clean/dirty.
import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useFormatDay, useHeader, useToday } from '../hooks';
import { pantryKey, useShopping } from '../store/shopping';
import { useAllIngredients, useAllUtensils, useLibrary } from '../store/library';
import { formatQuantity } from '../logic/format';
import { resolveUnit } from '../logic/units';
import Icon from '../components/Icon';
import SelectionBar, { useSelection } from '../components/SelectionBar';
import { EmptyState, IconButton, ListItem, Row, Screen, SectionHeader } from '../components/ui';
import { INGREDIENT_AISLES, UTENSIL_AISLES } from '../types';
import { spacing, usePalette } from '../theme';

export default function PantryScreen() {
  const formatDay = useFormatDay();
  const { t } = useTranslation();
  const p = usePalette();
  const today = useToday();
  const { pantry, removeFromPantry, setPantryDirty } = useShopping();
  const ingredients = useAllIngredients();
  const utensils = useAllUtensils();
  const units = useLibrary(s => s.units);
  const sel = useSelection();

  useHeader(
    {
      title: t('pantry.title'),
      headerRight: () =>
        sel.editing ? (
          <Row gap={spacing.m}>
            <IconButton icon="checklist" label={t('common.selectAll')} onPress={() => sel.setAll(pantry.items.map(pantryKey))} />
            <IconButton icon="check" label={t('common.done')} onPress={sel.stop} />
          </Row>
        ) : (
          <IconButton icon="edit" label={t('common.edit')} onPress={sel.start} />
        ),
    },
    [sel.editing, pantry, t],
  );

  if (!pantry.items.length) return <EmptyState icon="pantry" title={t('pantry.title')} body={t('pantry.empty')} />;

  const groups = [
    ...INGREDIENT_AISLES.map(a => ({ key: a, label: t(`aisles.${a}`), items: pantry.items.filter(i => i.kind === 'ingredient' && (ingredients[i.refId]?.aisle ?? 'other') === a) })),
    ...UTENSIL_AISLES.map(a => ({ key: `u.${a}`, label: t(`aisles.${a}`), items: pantry.items.filter(i => i.kind === 'utensil' && (utensils[i.refId]?.aisle ?? 'other') === a) })),
  ].filter(g => g.items.length);

  return (
    <View style={{ flex: 1 }}>
      <Screen>
        {groups.map(g => (
          <View key={g.key} style={{ gap: spacing.s }}>
            <SectionHeader title={g.label} />
            {g.items.map(item => {
              const key = pantryKey(item);
              if (item.kind === 'utensil') {
                return (
                  <ListItem
                    key={key}
                    title={utensils[item.refId]?.title ?? item.refId}
                    subtitle={`${t('pantry.available', { amount: item.amount })} · ${t('pantry.estimated')} - ${item.dirty ? t('pantry.dirty') : t('pantry.clean')}`}
                    icon="utensil"
                    selected={sel.editing ? sel.has(key) : undefined}
                    onPress={sel.editing ? () => sel.toggle(key) : () => setPantryDirty(item.refId, !item.dirty)}
                    right={<Icon name={item.dirty ? 'dirty' : 'checkCircle'} color={item.dirty ? p.warning : p.success} />}
                    chevron={false}
                  />
                );
              }
              const unit = resolveUnit(item.unit, units);
              const amount = `${formatQuantity(item.amount, unit)}${unit ? ` ${unit.symbol}` : ''}`;
              const expired = !!item.expiresOn && item.expiresOn < today;
              return (
                <ListItem
                  key={key}
                  title={ingredients[item.refId]?.title ?? item.refId}
                  subtitle={`${t('pantry.available', { amount })}${item.expiresOn ? ` · ${expired ? t('pantry.expired') : t('pantry.expires', { date: formatDay(item.expiresOn) })}` : ''}`}
                  icon="ingredient"
                  selected={sel.editing ? sel.has(key) : undefined}
                  onPress={sel.editing ? () => sel.toggle(key) : undefined}
                  right={<Icon name={expired ? 'warning' : 'clock'} color={expired ? p.danger : p.secondaryText} />}
                  chevron={false}
                />
              );
            })}
          </View>
        ))}
      </Screen>
      {sel.editing ? <SelectionBar count={sel.ids.size} onDelete={() => { removeFromPantry([...sel.ids]); sel.stop(); }} /> : null}
    </View>
  );
}
